/**
 * Language Switcher Component
 *
 * Handles WPML language switcher dropdown interactions.
 * Provides keyboard navigation and accessibility features.
 *
 * @package
 */

/**
 * LanguageSwitcher class
 *
 * Manages dropdown toggle, keyboard navigation, and accessibility.
 */
export class LanguageSwitcher {

	/**
	 * Private toggle button element
	 *
	 * @type {HTMLElement|null}
	 */
	#toggle = null;

	/**
	 * Private dropdown element
	 *
	 * @type {HTMLElement|null}
	 */
	#dropdown = null;

	/**
	 * Private dropdown open state
	 *
	 * @type {boolean}
	 */
	#isOpen = false;

	/**
	 * Constructor
	 *
	 * @param {HTMLElement} element The language switcher container element.
	 */
	constructor( element ) {

		this.element = element;
		this.#toggle = element.querySelector( '.language-switcher__toggle' );
		this.#dropdown = element.querySelector( '.language-switcher__dropdown' );

		if ( ! this.#toggle || ! this.#dropdown ) {

			return;

		}

		this.init();

	}

	/**
	 * Initialize event listeners
	 *
	 * @return {void}
	 */
	init = () => {

		this.#toggle.addEventListener( 'click', this.toggleDropdown );
		document.addEventListener( 'keydown', this.handleEscape );

	}

	/**
	 * Toggle dropdown visibility
	 *
	 * @param {Event} e Click event.
	 * @return {void}
	 */
	toggleDropdown = ( e ) => {

		e?.preventDefault();
		this.#isOpen = ! this.#isOpen;

		this.#toggle.setAttribute( 'aria-expanded', String( this.#isOpen ) );

		if ( this.#isOpen ) {

			this.#dropdown.removeAttribute( 'hidden' );
			document.addEventListener( 'click', this.handleOutsideClick );

		} else {

			this.#dropdown.setAttribute( 'hidden', '' );
			document.removeEventListener( 'click', this.handleOutsideClick );

		}

	}

	/**
	 * Handle clicks outside the dropdown
	 *
	 * Closes dropdown if clicked outside.
	 *
	 * @param {Event} e Click event.
	 * @return {void}
	 */
	handleOutsideClick = ( e ) => {

		if ( ! this.element.contains( e.target ) && this.#isOpen ) {

			this.toggleDropdown();

		}

	}

	/**
	 * Handle Escape key press
	 *
	 * Closes dropdown and returns focus to toggle button.
	 *
	 * @param {KeyboardEvent} e Keyboard event.
	 * @return {void}
	 */
	handleEscape = ( e ) => {

		if ( e.key === 'Escape' && this.#isOpen ) {

			this.toggleDropdown();
			this.#toggle.focus();

		}

	}

}

